import { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';

export const useQueryParams = () => {
  const location = useLocation();
  const history = useHistory();
  const [queryString, setQueryString] = useState<string>(location.search);

  const getQueryParams = (queryString: string): Record<string, string[]> => {
    const parsed = qs.parse(queryString, {
      ignoreQueryPrefix: true,
    });

    const queryParams: Record<string, string[]> = {};

    Object.entries(parsed).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        queryParams[key] = value.filter(
          (v): v is string => typeof v === 'string'
        );
      } else if (typeof value === 'string') {
        // Backward compatibility: handle old comma-separated format
        if (value.includes(',')) {
          queryParams[key] = value.split(',').filter((v) => v !== '');
        } else {
          queryParams[key] = [value];
        }
      }
    });

    return queryParams;
  };

  const setQueryParams = (
    updates: Record<string, string | string[] | null>
  ) => {
    const currentParams = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    const updatedParams = { ...currentParams };

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        delete updatedParams[key];
      } else if (Array.isArray(value)) {
        const filtered = value.filter(
          (v) => v !== null && v !== undefined && v !== ''
        );
        if (filtered.length === 0) {
          delete updatedParams[key];
        } else {
          updatedParams[key] = filtered;
        }
      } else {
        updatedParams[key] = value;
      }
    });

    const updatedQueryString = qs.stringify(updatedParams, {
      arrayFormat: 'repeat',
      addQueryPrefix: true,
      encode: true,
    });

    history.replace({ search: updatedQueryString });
    setQueryString(updatedQueryString);
  };

  useEffect(() => {
    setQueryString(location.search);
  }, [location.search]);

  return {
    queryString,
    queryParams: getQueryParams(queryString),
    setQueryParams,
  };
};
